import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Pack.module.scss';
import JoinPackConfirmationModal from './JoinPackConfirmationModal';
import Button from '../../components/Button';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { gqlTypes } from '../../types';
import { getPackQuery, joinPackMutation, userPetsIdQuery } from '../../graphql-operations';
import { Image } from '../../components/Image';
import CollarHero from '../../assets/images/collar_hero_blue_3.png';
import { getLoadingOrErrorElement } from '../../lib/util/getLoadingOrErrorElement';
import { joinPack as joinPackEvents } from '../../lib/analytics/events';
import ErrorMessage, { useError } from '../../components/ErrorMessage';
import AppPaths from '../../AppPaths';

export default function JoinPack() {
  const { t } = useTranslation();
  useEffect(() => joinPackEvents.viewed(), []);

  const { error, errorID, setError } = useError();

  const { packId } = useParams<{ packId: string }>();
  const petsQuery = useUserPetsQuery(setError);

  const getPackQueryResult = useGetPackQuery(packId, setError);
  const packName = getPackQueryResult.data?.pack?.name;

  const petIds =
    petsQuery.data?.currentUser?.userHouseholds.flatMap((household) => household.household.pets.map((pet) => pet.id)) ||
    [];

  const joinPack = useJoinPackMutation(setError);

  const triggerJoinPackModal = (
    <Button
      className={styles.simpleButton}
      disabled={getPackQueryResult.loading}
      onClick={() => joinPackEvents.confirmationModalOpen()}
    >
      {t('join_the_pack')}
    </Button>
  );

  const getPetsLoadingOrErrorElement = getLoadingOrErrorElement(petsQuery.loading, petsQuery.error);
  if (getPetsLoadingOrErrorElement) {
    return getPetsLoadingOrErrorElement;
  }

  return (
    <>
      <div className={styles.simpleFullWidthWrapper}>
        <div className={styles.simpleContainer}>
          <div className={styles.specialOfferChip} />
          <div className={styles.simpleHeaderContainer}>
            <h1 className={styles.simpleHeader}>{t('join_the_pack')}</h1>
            <p className={styles.simpleSubheader}>{packName}</p>
          </div>
          <div className={styles.collarContainer}>
            <Image className={styles.collarImage} image={{ type: 'image', url: CollarHero }} />
          </div>
          <JoinPackConfirmationModal
            packName={packName}
            trigger={triggerJoinPackModal}
            onAccept={() => {
              joinPack(petIds, packId, packName);
            }}
            onDecline={() => {
              joinPackEvents.confirmationModalGoBack();
            }}
          />
          {error && <ErrorMessage errors={[error]} errorID={errorID} />}
        </div>
      </div>
    </>
  );
}

function useUserPetsQuery(setError: (error: string) => void) {
  return useQuery<gqlTypes.userPets, gqlTypes.userPetsVariables>(userPetsIdQuery, {
    onError(err) {
      setError(err.message);
    },
  });
}

function useGetPackQuery(packId: string, setError: (error: string) => void) {
  return useQuery<gqlTypes.ECOMMERCE_pack, gqlTypes.ECOMMERCE_packVariables>(getPackQuery, {
    variables: { packId },
    onError(err) {
      setError(err.message);
    },
  });
}

function useJoinPackMutation(setError: (error: string) => void) {
  const { t } = useTranslation();
  const history = useHistory();

  const [partialJoinPackMutation] = useMutation<gqlTypes.ECOMMERCE_joinPack, gqlTypes.ECOMMERCE_joinPackVariables>(
    joinPackMutation,
  );

  return (petIds: string[], packId: string, packName?: string) => {
    if (!petIds.length) {
      setError(t('no_pets_found_with_active_subscription'));
      return;
    }

    Promise.all(
      petIds.map((petId) => {
        return partialJoinPackMutation({
          variables: { petId, packId },
        });
      }),
    )
      .then(() => {
        history.push({
          pathname: AppPaths.Packs.Complete(packId),
          search: window.location.search,
          state: { packName: packName },
        });
      })
      .catch((err) => {
        setError(err.message);
      });
  };
}
